import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID, computed, signal } from '@angular/core';

import { AuthUserModel } from './auth-user.model';

const AUTH_USER_STORAGE_KEY = 'auth-user';

@Injectable({
  providedIn: 'root',
})
export class AuthStateService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly authUserSignal = signal<AuthUserModel | null>(null);

  public readonly authUser = this.authUserSignal.asReadonly();
  public readonly token = computed(() => this.authUserSignal()?.token ?? null);
  public readonly isAuthenticated = computed(() => this.authUserSignal() !== null);

  public constructor() {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const persistedAuthUser = localStorage.getItem(AUTH_USER_STORAGE_KEY);
    if (!persistedAuthUser) {
      return;
    }

    try {
      this.authUserSignal.set(JSON.parse(persistedAuthUser) as AuthUserModel);
    } catch {
      localStorage.removeItem(AUTH_USER_STORAGE_KEY);
    }
  }

  public setAuthUser(authUser: AuthUserModel): void {
    this.authUserSignal.set(authUser);

    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(AUTH_USER_STORAGE_KEY, JSON.stringify(authUser));
    }
  }

  public clearAuth(): void {
    this.authUserSignal.set(null);

    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem(AUTH_USER_STORAGE_KEY);
    }
  }
}
